import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Clock, Calendar, RotateCcw, Loader2, FileText, ShoppingCart, X } from 'lucide-react';
import Button from '../common/Button';
import api from '../../utils/api';
import { getTimeUntilDeadline, isCheckoutExpired } from '../../hooks/useSuggestedCourses';
import CountdownBadge from '../common/CountdownBadge';
import PolicyModal from './PolicyModal';
import RefundModal from './RefundModal';
import { formatDays, formatTime, formatFee, formatDate, isStartDatePassed } from './courseHelpers';

export default function CourseCheckboxItem({ course, isSelected, onToggle, onRefundSuccess }) {
  const { t } = useTranslation('planner');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const schedule = course.schedule;
  const scheduleDisplay = schedule
    ? `${formatDays(schedule.days)} ${formatTime(schedule.start_time)} - ${formatTime(schedule.end_time)}`.trim()
    : t('course.scheduleTBD');

  const tuitionFee = formatFee(course.tuition_fee);
  const textbookFee = formatFee(course.textbook_fee);
  const examFee = formatFee(course.exam_fee);

  const totalFee = (parseFloat(course.tuition_fee) || 0) +
                   (parseFloat(course.textbook_fee) || 0) +
                   (parseFloat(course.exam_fee) || 0);

  const isPrivateCourse = course.potential_course_type === 1;
  const isPaidOrPartiallyPaid = course.is_paid === 1 || course.is_paid === 2;

  // Check if checkout offer has expired (deadline: 11:59 PM ET day before start)
  const isOfferExpired = isCheckoutExpired(course.start_date);

  // Check if start_date has passed (today or earlier)
  const startDatePassed = isStartDatePassed(course.start_date);

  const isDisabled = isPaidOrPartiallyPaid || startDatePassed || isOfferExpired;

  const handleRefundButtonClick = (e) => {
    e.stopPropagation(); // Prevent card click
    setShowRefundModal(true);
  };

  const handlePolicyClick = (e) => {
    e.stopPropagation(); // Prevent card click
    setShowPolicyModal(true);
  };

  const handleRefundConfirm = async () => {
    if (!course.student_course_id) {
      setRefundError(t('refund.cannotProcess'));
      setShowRefundModal(false);
      return;
    }

    setRefundLoading(true);
    setRefundError(null);

    try {
      await api.post(`/student-courses/${course.student_course_id}/refund`);
      setShowRefundModal(false);
      if (onRefundSuccess) {
        onRefundSuccess(course.student_course_id);
      }
    } catch (err) {
      console.error('Refund error:', err);
      setRefundError(err.response?.data?.error || t('refund.failed'));
      setShowRefundModal(false);
    } finally {
      setRefundLoading(false);
    }
  };

  // Show refund button only for fully paid courses with Stripe payment (not already refunded)
  const canRequestRefund = course.is_paid === 2 &&
                           course.stripe_payment_intent_id &&
                           course.refund_status !== 'full';

  // Cart button component to avoid duplication
  const CartButton = () => {
    if (isDisabled) return null;

    return isSelected ? (
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="!py-1 !px-2 !text-[11px] sm:!text-xs !text-red-500 !border-red-300 hover:!bg-red-50 hover:!border-red-400 dark:!border-red-500/50 dark:hover:!bg-red-900/30"
      >
        {t('cart.removeFromCart')}
      </Button>
    ) : (
      <Button
        variant="primary"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="!py-1 !px-2 !text-[11px] sm:!text-xs"
      >
        {t('cart.addToCart')}
      </Button>
    );
  };

  // Refund status component
  const RefundStatus = () => {
    if (!isDisabled) return null;
    if (!(refundLoading || course.refund_status === 'full' || (canRequestRefund && !refundError) || refundError)) return null;

    return (
      <>
        {refundLoading && (
          <div className="px-2 py-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-transparent dark:border dark:border-amber-600 rounded-md flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span className="hidden xs:inline">{t('refund.refundInitiated')}</span>
            <span className="xs:hidden">{t('refund.processing')}</span>
          </div>
        )}
        {!refundLoading && canRequestRefund && !refundError && (
          <button
            onClick={handleRefundButtonClick}
            className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 bg-gray-50 dark:bg-transparent dark:border dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" />
            <span className="hidden xs:inline">{t('refund.requestRefund')}</span>
            <span className="xs:hidden">{t('refund.refund')}</span>
          </button>
        )}
        {refundError && (
          <button
            onClick={(e) => { e.stopPropagation(); setRefundError(null); }}
            className="px-2 py-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-transparent dark:border dark:border-red-500 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30"
          >
            {refundError} ✕
          </button>
        )}
      </>
    );
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl border transition-all duration-200 ${
        isDisabled
          ? 'border-gray-200 dark:border-gray-700 opacity-60'
          : isSelected
            ? 'border-[#589DD5] ring-2 ring-[#589DD5]/20 shadow-md'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
      }`}
    >
      <div className="p-3 sm:p-4">
        {/* Top section: Icon + Course Info */}
        <div className="flex gap-2 sm:gap-3">
          {/* Course Icon */}
          <div className="shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#589DD5]/10 text-[#589DD5] flex items-center justify-center">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
          </div>

          {/* Course Content */}
          <div className="flex-1 min-w-0">
            {/* Title row with View Policy */}
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base break-words">
                {course.course_title}
              </h4>
              <button
                onClick={handlePolicyClick}
                className="shrink-0 text-[11px] sm:text-xs text-[#589DD5] hover:text-[#4080B0] dark:text-[#7AB8E8] dark:hover:text-[#9CCBF0] font-medium transition-all flex items-center gap-1 group"
              >
                <FileText className="w-3 h-3 group-hover:scale-110 transition-transform" />
                <span className="hidden xs:inline group-hover:underline">{t('course.viewPolicy')}</span>
                <span className="xs:hidden group-hover:underline">{t('course.policy')}</span>
              </button>
            </div>

            {/* Status badges - wrap on mobile */}
            <div className="flex flex-wrap gap-1 sm:gap-1.5 mt-1.5">
              {isPrivateCourse ? (
                <span className={`px-1.5 sm:px-2 py-0.5 bg-purple-100 dark:bg-transparent dark:border dark:border-purple-500 text-purple-700 dark:text-purple-400 text-[10px] sm:text-xs font-medium rounded-full ${startDatePassed || isOfferExpired ? 'opacity-50' : ''}`}>
                  {t('courseType.private')}
                </span>
              ) : (
                <span className={`px-1.5 sm:px-2 py-0.5 bg-indigo-100 dark:bg-transparent dark:border dark:border-indigo-500 text-indigo-700 dark:text-indigo-400 text-[10px] sm:text-xs font-medium rounded-full ${startDatePassed || isOfferExpired ? 'opacity-50' : ''}`}>
                  {t('courseType.group')}
                </span>
              )}
              {/* Course Started badge - only for paid/confirmed courses */}
              {startDatePassed && isPaidOrPartiallyPaid && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-orange-100 dark:bg-transparent dark:border dark:border-orange-500 text-orange-700 dark:text-orange-400 text-[10px] sm:text-xs font-medium rounded-full">
                  <span className="hidden sm:inline">{t('status.courseStarted')}</span>
                  <span className="sm:hidden">{t('status.started')}</span>
                </span>
              )}
              {/* Offer Ended badge - for unpaid/unconfirmed courses when offer expired OR course started */}
              {(isOfferExpired || startDatePassed) && !isPaidOrPartiallyPaid && !course.is_confirmed && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-gray-200 dark:bg-transparent dark:border dark:border-gray-500 text-gray-600 dark:text-gray-400 text-[10px] sm:text-xs font-medium rounded-full">
                  {t('status.offerEnded')}
                </span>
              )}
              {/* Checkout deadline badge - only show for unpurchased courses that haven't expired */}
              {!startDatePassed && !isOfferExpired && !course.is_paid && !course.is_confirmed && getTimeUntilDeadline(course.start_date) > 0 && (
                <CountdownBadge startDate={course.start_date} />
              )}
            </div>

            {/* Schedule info */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-x-4 sm:gap-y-1 mt-2 text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span className="break-words">{scheduleDisplay}</span>
                {schedule && <span className="text-gray-400 dark:text-gray-500">{t('course.timezone')}</span>}
              </span>
              {course.start_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                  {t('course.starts', { date: formatDate(course.start_date) })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Fees section - full width, aligned with card edges */}
        {totalFee > 0 && (
          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex flex-wrap gap-2 sm:gap-3 text-[11px] sm:text-xs">
              {tuitionFee && (
                <span className="text-gray-600 dark:text-gray-400">
                  {t('fees.tuition')} <span className="font-medium text-gray-900 dark:text-white">{tuitionFee}</span>
                </span>
              )}
              {textbookFee && (
                <span className="text-gray-600 dark:text-gray-400">
                  {t('fees.book')} <span className="font-medium text-gray-900 dark:text-white">{textbookFee}</span>
                </span>
              )}
              {examFee && (
                <span className="text-gray-600 dark:text-gray-400">
                  {t('fees.exam')} <span className="font-medium text-gray-900 dark:text-white">{examFee}</span>
                </span>
              )}
            </div>
            {/* Total + Cart Button row */}
            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="text-xs sm:text-sm font-semibold text-[#589DD5]">
                {t('fees.total')} ${totalFee.toFixed(2)}
              </div>
              <div className="flex items-center gap-2">
                <CartButton />
                <RefundStatus />
              </div>
            </div>
          </div>
        )}

        {/* Cart Button when no fees - full width */}
        {totalFee === 0 && (
          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
            <CartButton />
            <RefundStatus />
          </div>
        )}
      </div>

      {/* Refund Confirmation Modal */}
      <RefundModal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        onConfirm={handleRefundConfirm}
        course={course}
        refundAmount={totalFee}
        isLoading={refundLoading}
      />

      {/* Policy Modal */}
      <PolicyModal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        policy={course.policy}
        courseName={course.course_title}
        customPolicyText={course.custom_policy_text}
        customPolicyTextKr={course.custom_policy_text_kr}
      />
    </div>
  );
}
